import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import DashboardBuilder from './components/DashboardBuilder';
import AnalyticsCharts from './components/AnalyticsCharts';
import ReportGenerator from './components/ReportGenerator';
import TemplateLibrary from './components/TemplateLibrary';
import { analyticsService } from '../../services/analyticsService';
import { projectService } from '../../services/projectService';
import toast, { Toaster } from 'react-hot-toast';

const AdvancedAnalyticsDashboard = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('analytics');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    endDate: new Date()?.toISOString()?.split('T')?.[0]
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      loadAnalytics();
    }
  }, [selectedProject, dateRange]);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService?.getProjects();
      setProjects(projectsData);
      if (projectsData?.length > 0) {
        setSelectedProject(projectsData?.[0]?.id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      toast?.error('Error al cargar proyectos');
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await analyticsService?.getDashboardAnalytics({
        projectId: selectedProject,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate
      });
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast?.error('Error al cargar analíticas');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      await analyticsService?.exportAnalyticsData({
        projectId: selectedProject,
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate
      }, format);
      toast?.success(`Datos exportados en formato ${format?.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast?.error('Error al exportar datos');
    }
  };

  const tabs = [
    { id: 'analytics', label: 'Analíticas', icon: 'BarChart3' },
    { id: 'builder', label: 'Constructor', icon: 'LayoutGrid' },
    { id: 'reports', label: 'Reportes', icon: 'FileText' },
    { id: 'templates', label: 'Plantillas', icon: 'Layers' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />
      {/* Header - Mobile optimized */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="TrendingUp" size={20} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground font-heading truncate">
                  Analíticas Avanzadas
                </h1>
                <p className="text-xs sm:text-sm text-muted-foreground font-body">
                  Constructor de dashboards personalizados y reportes
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <select
                value={selectedProject || ''}
                onChange={(e) => setSelectedProject(e?.target?.value)}
                className="px-3 sm:px-4 py-2 bg-background border border-border rounded-lg text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary flex-1 sm:flex-initial"
              >
                <option value="">Todos los proyectos</option>
                {projects?.map(project => (
                  <option key={project?.id} value={project?.id}>
                    {project?.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleExport('csv')}
                className="px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-smooth flex items-center justify-center text-xs sm:text-sm touch-target"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Exportar
              </button>
            </div>
          </div>

          {/* Tabs - Mobile optimized with horizontal scroll */}
          <div className="flex space-x-1 mt-4 sm:mt-6 border-b border-border overflow-x-auto pb-2">
            {tabs?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`px-3 sm:px-4 py-2 flex items-center transition-smooth whitespace-nowrap text-xs sm:text-sm touch-target ${
                  activeTab === tab?.id
                    ? 'border-b-2 border-primary text-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} className="mr-2 flex-shrink-0" />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Date Range Filter - Mobile optimized */}
        <div className="bg-card rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 border border-border">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center">
              <Icon name="Calendar" size={16} className="mr-2 text-muted-foreground flex-shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-foreground">Rango de fechas:</span>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <input
                type="date"
                value={dateRange?.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e?.target?.value })}
                className="px-3 py-2 bg-background border border-border rounded-lg text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="text-muted-foreground text-center sm:text-left">-</span>
              <input
                type="date"
                value={dateRange?.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e?.target?.value })}
                className="px-3 py-2 bg-background border border-border rounded-lg text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {activeTab === 'analytics' && (
              <AnalyticsCharts 
                analyticsData={analyticsData} 
                projectId={selectedProject}
                dateRange={dateRange}
              />
            )}
            {activeTab === 'builder' && (
              <DashboardBuilder 
                projectId={selectedProject}
                userProfile={userProfile}
              />
            )}
            {activeTab === 'reports' && (
              <ReportGenerator 
                analyticsData={analyticsData}
                projectId={selectedProject}
                dateRange={dateRange}
              />
            )}
            {activeTab === 'templates' && (
              <TemplateLibrary 
                userProfile={userProfile}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdvancedAnalyticsDashboard;